import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { AskResponse, ask } from "@/api/bkca";
import { pickCode } from "@/features/services";
import {
  PERSONA,
  PersonaKind,
  greeting,
  personaGreeting,
  personaQueue,
  personaSummary,
  picksForVehicle,
  vehicleFromAnswer,
} from "@/features/assistant/persona";
import { assistantUserId, useProfileStore } from "@/store/profile";
import { useUiStore } from "@/store/ui";

export interface ChatMessage {
  id: string;
  who: "bot" | "me";
  text: string;
  time: string;
  pending?: boolean;
  failed?: boolean;
  /** То, на чём стоит ответ, — для `?debug=1`. */
  meta?: Pick<
    AskResponse,
    "branch" | "intent" | "why" | "blocked" | "numbers" | "context"
  >;
}

interface ChatState {
  messages: ChatMessage[];
  started: boolean;
  sending: boolean;
  personaQueue: PersonaKind[];
  personaIndex: number;
  start: () => void;
  send: (text: string) => Promise<void>;
  reset: () => void;
}

let counter = 0;
const nextId = () => `m${Date.now().toString(36)}${(counter += 1)}`;
const now = () =>
  new Date().toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const FAILED_TEXT =
  "Сейчас не могу посмотреть цифры — попробуйте через минуту.";

// Сколько реплик держим в истории: дальше лента и localStorage только пухнут.
const HISTORY_LIMIT = 200;

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => {
      const push = (msg: Omit<ChatMessage, "id" | "time">) => {
        const full: ChatMessage = { ...msg, id: nextId(), time: now() };
        set((s) => ({ messages: [...s.messages, full].slice(-HISTORY_LIMIT) }));
        return full.id;
      };
      const patch = (id: string, upd: Partial<ChatMessage>) =>
        set((s) => ({
          messages: s.messages.map((m) =>
            m.id === id ? { ...m, ...upd, time: now() } : m
          ),
        }));

      /** Ответ бота «с задержкой»: сначала скелетон, потом текст — как в прототипе. */
      const botLater = async (text: string, ms = 700) => {
        const id = push({ who: "bot", text: "", pending: true });
        await wait(ms);
        patch(id, { text, pending: false });
      };

      const personaActive = () => {
        const { personaQueue: q, personaIndex } = get();
        return (
          !useProfileStore.getState().personaDone && personaIndex < q.length
        );
      };

      const askAssistant = async (question: string) => {
        const profile = useProfileStore.getState();
        const debug = useUiStore.getState().debug;
        const id = push({ who: "bot", text: "", pending: true });
        set({ sending: true });
        try {
          const res = await ask({
            question,
            user_id: assistantUserId(),
            city: profile.city,
            tariffs: profile.picks.map(pickCode),
            include: debug ? ["context", "evidence"] : ["context"],
          });
          patch(id, {
            text: res.text,
            pending: false,
            meta: {
              branch: res.branch,
              intent: res.intent,
              why: res.why,
              blocked: res.blocked,
              numbers: res.numbers,
              context: res.context,
            },
          });
        } catch {
          patch(id, { text: FAILED_TEXT, pending: false, failed: true });
        } finally {
          set({ sending: false });
        }
      };

      const personaAnswer = async (answer: string) => {
        const profile = useProfileStore.getState();
        const { personaQueue: q, personaIndex } = get();
        const step = PERSONA[q[personaIndex]];

        if (step.kind === "name") profile.setName(answer.trim() || null);
        if (step.kind === "vehicle") {
          const vehicle = vehicleFromAnswer(answer);
          profile.setPersona({ vehicle });
          if (!profile.picksChosen)
            profile.setPicks(picksForVehicle(vehicle), false);
        }
        if (step.kind === "hours") profile.setPersona({ hours: answer });

        const nextIndex = personaIndex + 1;
        set({ personaIndex: nextIndex });
        let reply = step.reply(answer);
        if (nextIndex < q.length) reply += ` ${PERSONA[q[nextIndex]].ask}`;
        await botLater(reply);

        if (nextIndex >= q.length) {
          const p = useProfileStore.getState();
          p.setPersonaDone(true);
          await botLater(
            personaSummary({
              name: p.name,
              vehicle: p.persona.vehicle,
              hours: p.persona.hours,
              picks: p.picks,
            }),
            600
          );
        }
      };

      return {
        messages: [],
        started: false,
        sending: false,
        personaQueue: [],
        personaIndex: 0,

        start: () => {
          if (get().started) return;
          const profile = useProfileStore.getState();
          if (profile.personaDone) {
            set({ started: true, personaQueue: [], personaIndex: 0 });
            push({ who: "bot", text: greeting(profile.name) });
            return;
          }
          const q = personaQueue(profile);
          set({ started: true, personaQueue: q, personaIndex: 0 });
          push({ who: "bot", text: personaGreeting(profile) });
          push({ who: "bot", text: PERSONA[q[0]].ask });
        },

        send: async (text) => {
          const value = text.trim();
          if (!value || get().sending) return;
          push({ who: "me", text: value });
          if (personaActive()) {
            set({ sending: true });
            try {
              await personaAnswer(value);
            } finally {
              set({ sending: false });
            }
            return;
          }
          await askAssistant(value);
        },

        reset: () =>
          set({
            messages: [],
            started: false,
            personaQueue: [],
            personaIndex: 0,
          }),
      };
    },
    {
      name: "sd-chat-v1",
      storage: createJSONStorage(() => localStorage),
      // `sending` — не состояние сессии, а состояние запроса; его не сохраняем.
      partialize: (s) => ({
        messages: s.messages,
        started: s.started,
        personaQueue: s.personaQueue,
        personaIndex: s.personaIndex,
      }),
      // Ответ, не доехавший до перезагрузки, уже не доедет: скелетон вечно
      // крутиться не должен, показываем его как неудавшийся.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const stale = state.messages.some((m) => m.pending);
        if (stale) {
          state.messages = state.messages.map((m) =>
            m.pending
              ? { ...m, pending: false, failed: true, text: FAILED_TEXT }
              : m
          );
        }
      },
    }
  )
);
